import React, { useState } from 'react';
import Layout from '../components/layout/layout';
import { Table, Button, Drawer, Label, Textarea, TextInput, Modal, FileInput, Select } from "flowbite-react";
import { HiOutlineExclamationCircle, HiMiniTrash, HiDocumentDuplicate } from "react-icons/hi2";
import { Switch } from '@headlessui/react';
import { useLocation } from 'react-router-dom';
import { useCategories } from '../hooks/useCategories';
import { useToastHandler } from '../hooks/useToastHandler';
import { updateDatabase } from '../utils/updateDatabase';
import { useAuth } from '../hooks/use-auth';

function Category() {
    const location = useLocation();
    const currentId = location.pathname.split('/').pop();

    const [categories, setCategories, setUpdateEffect] = useCategories(currentId);
    const showToast = useToastHandler();
    const { session } = useAuth();

    const [draggedItem, setDraggedItem] = useState(null);
    const [isOpen, setIsOpen] = useState(false);
    const [openModalId, setOpenModalId] = useState(null);
    const [editingCategory, setEditingCategory] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isTranslating, setIsTranslating] = useState(false);
    const token = localStorage.getItem('authToken');

    const [formData, setFormData] = useState({
        roName: '',
        enName: '',
        roDescription: '',
        enDescription: '',
        roNutrition: '',
        enNutrition: '',
        price: '',
        image: '',
        subcategories: currentId
    });

    const [cloneModalOpen, setCloneModalOpen] = useState(false);
    const [productToClone, setProductToClone] = useState(null);
    const [targetSubcategory, setTargetSubcategory] = useState('');
    const [structure, setStructure] = useState([]);
    const [isLoadingStructure, setIsLoadingStructure] = useState(false);

    // Check if current user has permission to edit/delete products
    const hasEditPermission = () => {
        if (!session || !session.user) return false;
        return session.user.id === 1 || session.user.id === 7;
    };

    function classNames(...classes) {
        return classes.filter(Boolean).join(' ')
    }

    const customDrawer = {
        "root": {
            "base": "fixed z-[60] overflow-y-auto bg-white p-4 transition-transform dark:bg-gray-800",
            "backdrop": "fixed inset-0 z-[55] bg-gray-900/50 dark:bg-gray-900/80",
            "position": {
                "right": {
                    "on": "right-0 top-0 h-screen w-full lg:w-1/3 transform-none",
                    "off": "right-0 top-0 h-screen w-full lg:w-1/3 translate-x-full"
                }
            }
        }
    };

    const handleDragStart = (e, item) => {
        if (!hasEditPermission()) {
            e.preventDefault();
            return;
        }
        setDraggedItem(item);
    };

    const handleDrop = (e, dropTarget) => {
        if (!hasEditPermission()) {
            e.preventDefault();
            return;
        }
        e.preventDefault();
        const newCategories = reorderCategories(draggedItem, dropTarget);
        setCategories(newCategories);
        setDraggedItem(null);
        updateDatabase(newCategories);
    };

    const reorderCategories = (draggedItem, dropTarget) => {
        const newCategories = [...categories];
        const draggedIndex = newCategories.findIndex(c => c.id === draggedItem.id);
        const targetIndex = newCategories.findIndex(c => c.id === dropTarget.id);
        newCategories.splice(draggedIndex, 1);
        newCategories.splice(targetIndex, 0, draggedItem);

        return newCategories.map((category, index) => ({ ...category, order: index }));
    };

    const handleFileChange = (e) => {
        e.preventDefault();
        const file = e.target.files[0];
        const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
        const maxSize = 25 * 1024 * 1024;

        if (file) {
            if (!allowedTypes.includes(file.type)) {
                showToast("Invalid File Type", "Only JPG, JPEG, PNG, and WEBP files are allowed.", 'destructive');
            } else if (file.size > maxSize) {
                showToast("File Too Large", "The file size should not exceed 25MB.", 'destructive');
            } else {
                setSelectedFile(file);
                setPreviewUrl(URL.createObjectURL(file));
                e.target.value = null; // Clear the file input
            }
        }
    };

    const handleEdit = async (category) => {
        setPreviewUrl(null);
        setEditingCategory(category);
        setIsOpen(true);
        setFormData({
            roName: category.roName || '',
            enName: category.enName || '',
            roDescription: category.roDescription || '',
            enDescription: category.enDescription || '',
            roNutrition: category.roNutrition || '',
            enNutrition: category.enNutrition || '',
            price: category.price || '',
            image: category.image || '',
            subcategories: category.subcategories || currentId,
            order: category.order || 0, // Ensure order is set correctly
        });
        if (category.image) {
            try {
                const imageUrl = `https://admin.perfettocucinaitaliana.ro/api/public/${category.image}-960.webp`;
                setPreviewUrl(imageUrl);
            } catch (error) {
                console.error('Failed to fetch image URL:', error);
            }
        }
    };

    const handleAdd = () => {
        const maxOrder = categories.reduce((max, category) => Math.max(max, category.order), -1);
        // Reset the formData to initial state when adding a new product
        setFormData({
            roName: '',
            enName: '',
            roDescription: '',
            enDescription: '',
            roNutrition: '',
            enNutrition: '',
            price: '',
            image: '',
            subcategories: currentId,
            order: maxOrder + 1
        });
        setSelectedFile(null);
        setPreviewUrl(null);
        setIsOpen(true);
        setEditingCategory(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!hasEditPermission()) {
            showToast("Access Denied", "You don't have permission to modify products", 'destructive');
            return;
        }

        if (formData.roName === '' || formData.enName === '') {
            showToast("Missing Data", "Please add all necessary information", 'destructive');
            return;
        }

        let newFormData = { ...formData };

        if (selectedFile) {
            setIsUploading(true);
            try {
                const formData = new FormData();
                formData.append('file', selectedFile);

                const response = await fetch('https://admin.perfettocucinaitaliana.ro/api/upload', {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }, body: formData
                });
                const data = await response.json();

                newFormData.image = data.id;
                setSelectedFile(null);
                setIsUploading(false);
            } catch (error) {
                showToast("Upload Error", error.message, 'destructive');
                setIsUploading(false);
                return;
            }
        }

        try {
            const method = editingCategory ? 'PUT' : 'POST';
            const url = editingCategory ? `https://admin.perfettocucinaitaliana.ro/api/documents/${editingCategory.id}` : 'https://admin.perfettocucinaitaliana.ro/api/documents';

            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newFormData)
            });

            if (!response.ok) throw new Error(`Failed to ${editingCategory ? 'update' : 'add'} document`);

            setIsOpen(false);
            setUpdateEffect(prev => !prev);
            setEditingCategory(null);
            showToast(`Produsul a fost ${editingCategory ? 'modificat' : 'adaugat'} cu succes`);
        } catch (error) {
            showToast("Error", `An error occurred while ${editingCategory ? 'updating' : 'adding'} the product, please contact the administrator`, 'destructive');
        }
    };

    const handleDelete = async (itemId) => {
        if (!hasEditPermission()) {
            showToast("Access Denied", "You don't have permission to delete products", 'destructive');
            return;
        }

        try {
            const response = await fetch(`https://admin.perfettocucinaitaliana.ro/api/documents/${itemId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                },
            });
            if (!response.ok) throw new Error('Failed to delete the item');

            setOpenModalId(null);
            setUpdateEffect(prev => !prev);
            showToast("Produsul a fost sters cu success");
        } catch (error) {
            showToast("Eroare", "Nu s-a putut sterge produsul, te rog sa contactezi administratorul", 'destructive');
        }
    };

    const handleToggle = async (categoryId, newToggleState, name) => {
        try {
            const updatedCategories = categories.map(category =>
                category.id === categoryId ? { ...category, visibility: newToggleState } : category
            );

            setCategories(updatedCategories);
            await updateDatabase(updatedCategories);

            // Send POST request with visibility change information
            const visibilityChangeData = {
                name: session?.user?.name || 'Unknown User',
                productName: name,
                visibility: newToggleState,
                time: new Date().toISOString()
            };

            // You can replace this URL with your desired endpoint
            fetch('https://robot.bdesk.ro/webhook/dcc7ab03-1731-4796-8509-70a7791905a4', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(visibilityChangeData)
            }).catch(error => {
                console.error('Failed to send visibility change notification:', error);
                // Don't show error to user as this is just a notification
            });

            showToast(name, "Vizibilitatea produsului a fost actualizata cu succes");
        } catch (error) {
            showToast("Error", "Failed to update toggle", 'destructive');
        }
    };

    const handleImageDelete = () => {
        setFormData({ ...formData, image: null });
        setSelectedFile(null);
        setPreviewUrl(null);
    };

    const handleTranslate = async (field) => {
        const romanianText = formData[`ro${field}`];
        if (!romanianText) {
            showToast("No text to translate", "Please enter Romanian text first.", 'destructive');
            return;
        }

        setIsTranslating(true);

        const options = {
            method: 'POST',
            headers: {
                Authorization: 'Bearer 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',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                "model": "r1-1776",
                "messages": [
                    { "role": "system", "content": "Translate the given text from Romanian to English and that's all, be concise." },
                    { "role": "user", "content": romanianText }
                ]
            })
        };

        try {
            const response = await fetch('https://api.perplexity.ai/chat/completions', options);
            const data = await response.json();
            const translatedText = data.choices[0].message.content
                .replace(/<think>[\s\S]*?<\/think>\s*/g, '')
                .trim();
            setFormData(prev => ({ ...prev, [`en${field}`]: translatedText }));
        } catch (err) {
            console.error(err);
            showToast("Translation Error", "Failed to translate the text. Please try again.", 'destructive');
        } finally {
            setIsTranslating(false);
        }
    };

    const fetchStructure = async () => {
        setIsLoadingStructure(true);
        try {
            const catRes = await fetch('https://admin.perfettocucinaitaliana.ro/api/categories');
            const catData = await catRes.json();
            const cats = catData.categories || [];

            const options = [];
            for (const cat of cats) {
                const subRes = await fetch(`https://admin.perfettocucinaitaliana.ro/api/subcategories?categoryId=${cat.id}`);
                const subData = await subRes.json();
                const subs = subData.subcategories || [];

                for (const sub of subs) {
                    options.push({
                        id: sub.id,
                        label: `${cat.roName} > ${sub.roName}`
                    });
                }
            }
            setStructure(options);
        } catch (error) {
            console.error("Failed to fetch structure", error);
            showToast("Error", "Failed to load categories", 'destructive');
        } finally {
            setIsLoadingStructure(false);
        }
    };

    const handleCloneClick = (product) => {
        setProductToClone(product);
        setTargetSubcategory('');
        setCloneModalOpen(true);
        if (structure.length === 0) {
            fetchStructure();
        }
    };

    const handleCloneSubmit = async () => {
        if (!targetSubcategory) {
            showToast("Error", "Please select a category", 'destructive');
            return;
        }

        setIsLoadingStructure(true);

        try {
            let newImageId = productToClone.image;

            if (productToClone.image) {
                try {
                    const imageUrl = `https://admin.perfettocucinaitaliana.ro/api/public/${productToClone.image}-960.webp`;
                    const imageResponse = await fetch(imageUrl);
                    const imageBlob = await imageResponse.blob();

                    const formData = new FormData();
                    formData.append('file', imageBlob, `cloned-${Date.now()}.webp`);

                    const uploadResponse = await fetch('https://admin.perfettocucinaitaliana.ro/api/upload', {
                        method: 'POST',
                        headers: {
                            'Authorization': `Bearer ${token}`
                        },
                        body: formData
                    });

                    if (uploadResponse.ok) {
                        const uploadData = await uploadResponse.json();
                        newImageId = uploadData.id;
                    }
                } catch (error) {
                    console.error("Failed to re-upload image:", error);
                }
            }

            const newProduct = {
                roName: productToClone.roName,
                enName: productToClone.enName,
                roDescription: productToClone.roDescription,
                enDescription: productToClone.enDescription,
                roNutrition: productToClone.roNutrition,
                enNutrition: productToClone.enNutrition,
                price: productToClone.price,
                image: newImageId,
                visibility: productToClone.visibility,
                order: 100,
                subcategories: targetSubcategory
            };

            const response = await fetch('https://admin.perfettocucinaitaliana.ro/api/documents', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(newProduct)
            });

            if (!response.ok) throw new Error('Failed to clone product');

            setCloneModalOpen(false);
            setProductToClone(null);
            showToast("Success", "Produsul a fost clonat cu succes");
            if (targetSubcategory === currentId) {
                setUpdateEffect(prev => !prev);
            }
        } catch (error) {
            showToast("Error", "Failed to clone product", 'destructive');
        } finally {
            setIsLoadingStructure(false);
        }
    };

    return (
        <Layout>
            <div className="flex justify-end w-full py-2 items-right">
                {hasEditPermission() && (
                    <Button color='light' onClick={handleAdd}>Adauga produs</Button>
                )}
            </div>
            <Drawer open={isOpen} onClose={() => setIsOpen(false)} position="right" theme={customDrawer}>
                <Drawer.Header title="Adauga produs" titleIcon={() => <></>} />
                <Drawer.Items>
                    <form onSubmit={handleSubmit}>
                        <div className="mt-3 mb-6">
                            <Label htmlFor="roName" className="block mb-2">Nume | RO</Label>
                            <TextInput id="roName" name="roName" placeholder="Nume romana" value={formData.roName} onChange={(e) => setFormData({ ...formData, roName: e.target.value })} />
                        </div>
                        <div className="mb-6">
                            <Label htmlFor="enName" className="block mb-2">Nume | EN</Label>
                            <TextInput id="enName" name="enName" placeholder="Nume engleza" value={formData.enName} onChange={(e) => setFormData({ ...formData, enName: e.target.value })} />
                        </div>
                        <div className="mb-6">
                            <Label htmlFor="roDescription" className="block mb-2">Descriere | RO</Label>
                            <Textarea id="roDescription" name="roDescription" placeholder="Descriere in romana" value={formData.roDescription} onChange={(e) => setFormData({ ...formData, roDescription: e.target.value })} />
                        </div>
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-2">
                                <Label htmlFor="enDescription" className="block">Descriere | EN</Label>
                                <Button
                                    type="button"
                                    size="xs"
                                    onClick={() => handleTranslate('Description')}
                                    disabled={isTranslating}
                                >
                                    {isTranslating ? (
                                        <div className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            AI traduce textul...
                                        </div>
                                    ) : (
                                        'Translate'
                                    )}
                                </Button>
                            </div>
                            <Textarea id="enDescription" name="enDescription" placeholder="Descriere in engleza" value={formData.enDescription} onChange={(e) => setFormData({ ...formData, enDescription: e.target.value })} />
                        </div>
                        <div className="mb-6">
                            <Label htmlFor="roNutrition" className="block mb-2">Alergeni | RO</Label>
                            <Textarea id="roNutrition" name="roNutrition" placeholder="Alergeni in romana" value={formData.roNutrition} onChange={(e) => setFormData({ ...formData, roNutrition: e.target.value })} />
                        </div>
                        <div className="mb-6">
                            <div className="flex items-center justify-between mb-2">
                                <Label htmlFor="enNutrition" className="block">Alergeni | EN</Label>
                                <Button
                                    type="button"
                                    size="xs"
                                    onClick={() => handleTranslate('Nutrition')}
                                    disabled={isTranslating}
                                >
                                    {isTranslating ? (
                                        <div className="flex items-center">
                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                            </svg>
                                            AI traduce textul...
                                        </div>
                                    ) : (
                                        'Translate'
                                    )}
                                </Button>
                            </div>
                            <Textarea id="enNutrition" name="enNutrition" placeholder="Alergeni in engleza" value={formData.enNutrition} onChange={(e) => setFormData({ ...formData, enNutrition: e.target.value })} />
                        </div>
                        <div className="mb-6">
                            <Label htmlFor="price" className="block mb-2">Pret</Label>
                            <TextInput id="price" name="price" placeholder="Pret" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
                        </div>
                        <Label
                            htmlFor="dropzone-file"
                            className={`flex flex-col items-center justify-center w-full h-64 border-2 overflow-hidden relative ${isDragOver ? 'border-blue-500' : 'border-gray-300'} border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:hover:border-gray-500 dark:hover:bg-gray-600`}
                            onDragOver={(e) => {
                                e.preventDefault();
                                setIsDragOver(true);
                            }}
                            onDragLeave={(e) => {
                                e.preventDefault();
                                setIsDragOver(false);
                            }}
                            onDrop={(e) => {
                                e.preventDefault();
                                setIsDragOver(false);
                                const file = e.dataTransfer.files[0];
                                setSelectedFile(file);
                                setPreviewUrl(URL.createObjectURL(file));
                            }}
                        >
                            {previewUrl ? (
                                <>
                                    <img src={previewUrl} alt="Preview" className='object-cover w-full h-full' />
                                    <HiMiniTrash type="button" className="absolute w-6 h-6 p-1 font-medium text-white bg-red-500 rounded-full top-1 right-1" onClick={handleImageDelete} />
                                </>
                            ) : (
                                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                                    <svg className="w-8 h-8 mb-4 text-gray-500 dark:text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                        <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2" />
                                    </svg>
                                    <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                                        <span className="font-semibold">Click to upload</span> or drag and drop
                                    </p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">JPG, JPEG, PNG, WEBP (MAX. 25MB)</p>
                                </div>
                            )}
                            <FileInput id="dropzone-file" className="hidden" onChange={handleFileChange} />
                        </Label>
                        <div className="my-6">
                            <Button color='light' type="submit" className="w-full" disabled={isUploading}>
                                {isUploading ? 'Uploading...' : (editingCategory ? 'Update' : 'Adauga')}
                            </Button>
                        </div>
                    </form>
                </Drawer.Items>
            </Drawer>
            <div className="overflow-x-auto">
                <Table hoverable>
                    <Table.Head>
                        <Table.HeadCell>Imagine</Table.HeadCell>
                        <Table.HeadCell>Nume produs</Table.HeadCell>
                        <Table.HeadCell>Pret</Table.HeadCell>
                        <Table.HeadCell>Vizibilitate</Table.HeadCell>
                        {hasEditPermission() && (
                            <Table.HeadCell><span className='sr-only'>Action</span></Table.HeadCell>
                        )}
                    </Table.Head>
                    <Table.Body className="divide-y">
                        {categories !== undefined && categories.map((category) => (
                            <Table.Row
                                key={category.id}
                                draggable={hasEditPermission()}
                                onDragStart={(e) => handleDragStart(e, category)}
                                onDragOver={(e) => e.preventDefault()}
                                onDrop={(e) => handleDrop(e, category)}
                                className={`bg-white dark:border-gray-700 dark:bg-gray-800 ${hasEditPermission() ? 'cursor-move' : ''}`}
                            >
                                <Table.Cell className='flex w-20 p-0 m-2 overflow-hidden rounded-lg aspect-square'>
                                    {category.image ? <img className='object-cover w-full rounded-sm' alt={category.name} src={`https://admin.perfettocucinaitaliana.ro/api/public/${category.image}-300.webp`} /> : null}
                                </Table.Cell>
                                <Table.Cell className={`font-medium text-gray-900 whitespace-nowrap dark:text-white ${hasEditPermission() ? 'cursor-move' : ''}`}>
                                    {category.roName}
                                </Table.Cell>
                                <Table.Cell><span className='font-bold '>{category.price}</span></Table.Cell>
                                <Table.Cell>
                                    <Switch
                                        checked={category.visibility}
                                        onChange={() => handleToggle(category.id, !category.visibility, category.roName)}
                                        className={classNames(
                                            category.visibility ? 'bg-indigo-600' : 'bg-gray-200',
                                            'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2'
                                        )}
                                    >
                                        <span className="sr-only">Use setting</span>
                                        <span
                                            className={classNames(
                                                category.visibility ? 'translate-x-5' : 'translate-x-0',
                                                'pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                                            )}
                                        >
                                            <span
                                                className={classNames(
                                                    category.visibility ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in',
                                                    'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
                                                )}
                                                aria-hidden="true"
                                            >
                                                <svg className="w-3 h-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                                                    <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                                                </svg>
                                            </span>
                                            <span
                                                className={classNames(
                                                    category.visibility ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out',
                                                    'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity'
                                                )}
                                                aria-hidden="true"
                                            >
                                                <svg className="w-3 h-3 text-indigo-600" fill="currentColor" viewBox="0 0 12 12">
                                                    <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                                                </svg>
                                            </span>
                                        </span>
                                    </Switch>
                                </Table.Cell>
                                {hasEditPermission() && (
                                    <Table.Cell>
                                        <div className='flex'>
                                            <Button color="dark" size="xs" className="ml-2" onClick={() => handleEdit(category)}>Edit</Button>
                                            <Button color="light" size="xs" className="ml-2" onClick={() => handleCloneClick(category)}>
                                                Clone
                                            </Button>
                                            <Button onClick={() => setOpenModalId(category.id)} color="failure" size="xs" className="ml-2">Delete</Button>
                                        </div>
                                        <Modal show={openModalId === category.id} size="md" onClose={() => setOpenModalId(null)} popup>
                                            <Modal.Header />
                                            <Modal.Body>
                                                <div className="text-center">
                                                    <HiOutlineExclamationCircle className="mx-auto mb-4 text-gray-400 h-14 w-14 dark:text-gray-200" />
                                                    <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                                                        Esti sigur ca vrei sa stergi acest produs? Actiunea este ireversibila.
                                                    </h3>
                                                    <div className="flex justify-center gap-4">
                                                        <Button color="failure" onClick={() => handleDelete(category.id)}>Da, sunt sigur</Button>
                                                        <Button color="gray" onClick={() => setOpenModalId(null)}>Nu, anuleaza</Button>
                                                    </div>
                                                </div>
                                            </Modal.Body>
                                        </Modal>

                                    </Table.Cell>
                                )}
                            </Table.Row>
                        ))}
                    </Table.Body>
                </Table>
            </div>

            {/* Clone Modal - Outside the table loop */}
            <Modal show={cloneModalOpen} size="md" onClose={() => setCloneModalOpen(false)} popup>
                <Modal.Header />
                <Modal.Body>
                    <div className="text-center">
                        <HiDocumentDuplicate className="mx-auto mb-4 text-gray-400 h-14 w-14 dark:text-gray-200" />
                        <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
                            Cloneaza produsul "{productToClone?.roName}"
                        </h3>
                        <div className="mb-6 text-left">
                            <Label htmlFor="targetCategory" value="Alege categoria destinatie" />
                            <Select
                                id="targetCategory"
                                required
                                value={targetSubcategory}
                                onChange={(e) => setTargetSubcategory(e.target.value)}
                                disabled={isLoadingStructure}
                            >
                                <option value="">Selecteaza o categorie...</option>
                                {structure.map((option) => (
                                    <option key={option.id} value={option.id}>
                                        {option.label}
                                    </option>
                                ))}
                            </Select>
                            {isLoadingStructure && <p className="text-sm text-gray-500 mt-1">Se incarca categoriile...</p>}
                        </div>
                        <div className="flex justify-center gap-4">
                            <Button color="success" onClick={handleCloneSubmit} disabled={isLoadingStructure || !targetSubcategory}>
                                Cloneaza
                            </Button>
                            <Button color="gray" onClick={() => setCloneModalOpen(false)}>
                                Anuleaza
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </Layout>
    );
}

export default Category;
